import type {ComponentStyle, CompRef, SingleLayoutData} from '@wix/document-services-types'
import type {ExtensionAPI} from '@wix/document-manager-core'
import type {VariantsExtensionAPI} from '../../variants/variants'
import {DATA_TYPES, COMP_TYPES} from '../../../constants/constants'
import {StyleTypes, type ThemeExtAPI} from '../../theme/theme'
import _ from 'lodash'
import type {StyleEntryItem} from '../conversionDal/types'
import type {DeepPartial, ConversionAlgoConfig} from '../types'
import type {BuilderComponentsExtensionAPI} from '../../builderComponents/builderComponents'
import type {StructureExtensionAPI} from '../../structure'

const oldModelStyleTransformer = ({cssCustomProperties, spx}: StyleEntryItem = {}):
    | DeepPartial<ComponentStyle>
    | undefined => {
    const cleanedCssCustomProperties = _.omitBy(cssCustomProperties, _.isNil)

    if (_.isEmpty(cleanedCssCustomProperties)) {
        return undefined
    }

    const style = {}

    for (const [key, value] of Object.entries(cleanedCssCustomProperties)) {
        _.set(style, key, value)
    }

    return {
        style,
        spx
    }
}

const cssStyleTransformer = (styleEntryItem: StyleEntryItem) => {
    const cleanedCssProperties = _.omitBy(styleEntryItem?.cssProperties, _.isNil)
    const cleanedCssCustomProperties = _.omitBy(styleEntryItem?.cssCustomProperties, _.isNil)

    if (_.isEmpty(cleanedCssProperties) && _.isEmpty(cleanedCssCustomProperties)) {
        return undefined
    }

    const result = {
        ...styleEntryItem
    }

    if (!_.isEmpty(cleanedCssProperties)) {
        result.cssProperties = cleanedCssProperties
    } else {
        delete result.cssProperties
    }

    if (!_.isEmpty(cleanedCssCustomProperties)) {
        result.cssCustomProperties = cleanedCssCustomProperties
    } else {
        delete result.cssCustomProperties
    }

    return result
}

const responsiveSectionLayoutTransformer = (
    extensionAPI: ExtensionAPI,
    compPointer: CompRef,
    conversionAlgoConfig: ConversionAlgoConfig,
    layoutToUpdate: SingleLayoutData
): SingleLayoutData => {
    const {components} = extensionAPI as StructureExtensionAPI
    const {variants} = extensionAPI as VariantsExtensionAPI

    if (
        conversionAlgoConfig.rootPointer.id === compPointer.id &&
        components.getComponentType(compPointer) === COMP_TYPES.RESPONSIVE_SECTION
    ) {
        const currentLayout: any = variants.getComponentItemConsideringVariants(compPointer, DATA_TYPES.layout)

        return {
            ...layoutToUpdate,
            itemLayout: currentLayout?.itemLayout?.type ? currentLayout?.itemLayout : {}
        }
    }

    return layoutToUpdate
}

export const getLayoutTransformer = (
    extensionAPI: ExtensionAPI,
    compPointer: CompRef,
    conversionAlgoConfig: ConversionAlgoConfig
) => {
    return (layoutToUpdate: SingleLayoutData): SingleLayoutData => {
        return responsiveSectionLayoutTransformer(extensionAPI, compPointer, conversionAlgoConfig, layoutToUpdate)
    }
}

export const getStyleTransformer = (
    extensionAPI: ExtensionAPI,
    compPointer: CompRef,
    styleItem: StyleEntryItem
): CallableFunction => {
    const compType = (extensionAPI as StructureExtensionAPI).components.getComponentType(compPointer)
    const isBuilder = (extensionAPI as BuilderComponentsExtensionAPI).builderComponents.isBuilder(compType)
    const styleTransformer = isBuilder ? cssStyleTransformer : oldModelStyleTransformer
    const {variants} = extensionAPI as VariantsExtensionAPI
    const {theme} = extensionAPI as ThemeExtAPI
    let baseStyleItem = variants.getComponentItemConsideringVariants(compPointer, DATA_TYPES.theme)

    if (!isBuilder && (!baseStyleItem || baseStyleItem.type === StyleTypes.topLevelStyle)) {
        baseStyleItem = theme.createComponentStyle(compType, '', {}, {}, {})
        delete baseStyleItem.skin
    }

    return () => {
        const transformedStyle = styleTransformer(styleItem)

        if (!transformedStyle) {
            return
        }

        if (isBuilder) {
            return transformedStyle
        }

        return _.merge({}, baseStyleItem, transformedStyle)
    }
}
